import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import categoryService from '../services/categoryService.js';

interface Category {
  id: number;
  name: string;
  name_ar?: string;
}

interface Product {
  id: number;
  product_name: string;
  product_name_ar?: string;
  category_id?: number;
  active_ingredient?: string;
  registration_number?: string;
  crop?: string;
  season_year?: string;
  usage_rate?: string;
  pest?: string;
  producing_company?: string;
  country?: string;
  exporting_company?: string;
  experimentation_26_25?: string;
  notes?: string;
  branch?: string;
  season?: string;
}

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Omit<Product, 'id'>) => void;
  product: Product | null;
}

export function ProductModal({ isOpen, onClose, onSave, product }: ProductModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [formData, setFormData] = useState({
    product_name: '',
    product_name_ar: '',
    category_id: undefined as number | undefined,
    active_ingredient: '',
    registration_number: '',
    crop: '',
    season_year: '',
    usage_rate: '',
    pest: '',
    producing_company: '',
    country: '',
    exporting_company: '',
    experimentation_26_25: '',
    notes: '',
    branch: '',
    season: '',
  });

  // Load categories when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name || '',
        product_name_ar: product.product_name_ar || '',
        category_id: product.category_id,
        active_ingredient: product.active_ingredient || '',
        registration_number: product.registration_number || '',
        crop: product.crop || '',
        season_year: product.season_year || '',
        usage_rate: product.usage_rate || '',
        pest: product.pest || '',
        producing_company: product.producing_company || '',
        country: product.country || '',
        exporting_company: product.exporting_company || '',
        experimentation_26_25: product.experimentation_26_25 || '',
        notes: product.notes || '',
        branch: product.branch || '',
        season: product.season || '',
      });
    } else {
      setFormData({
        product_name: '',
        product_name_ar: '',
        category_id: undefined,
        active_ingredient: '',
        registration_number: '',
        crop: '',
        season_year: '',
        usage_rate: '',
        pest: '',
        producing_company: '',
        country: '',
        exporting_company: '',
        experimentation_26_25: '',
        notes: '',
        branch: '',
        season: '',
      });
    }
  }, [product, isOpen]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryService.getCategories();
      // Ultra-robust filtering identical to ProductsTable to prevent SelectItem errors
      const allCategories = response?.data || response || [];
      const validCategories: Category[] = allCategories
        .filter((category: any) => {
          // Step-by-step validation to catch all edge cases
          if (category == null) return false; // null, undefined
          if (typeof category.id !== 'number') return false; // non-numeric id
          if (!category.name || typeof category.name !== 'string') return false; // invalid name
          const trimmedName = category.name.trim();
          return trimmedName.length > 0 && trimmedName !== ''; // not empty after trim
        })
        .map((category: any) => ({
          id: category.id,
          name: category.name.trim(),
          name_ar: category.name_ar ? category.name_ar.trim() : undefined
        }));

      console.log('ProductModal loaded categories:', validCategories); // Debug logging
      setCategories(validCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{product ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          <DialogDescription>
            {product ? 'Update product information below.' : 'Fill in the details to create a new product.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="product-name">Product Name (English)</Label>
              <Input
                id="product-name"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                placeholder="Enter product name"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="product-name-ar">Product Name (Arabic)</Label>
              <Input
                id="product-name-ar"
                value={formData.product_name_ar}
                onChange={(e) => setFormData({ ...formData, product_name_ar: e.target.value })}
                placeholder="Enter Arabic product name"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <Select
  value={formData.category_id != null ? formData.category_id.toString() : 'none'}
  onValueChange={(value) =>
    setFormData({ 
      ...formData, 
      category_id: value !== 'none' ? parseInt(value) : undefined 
    })
  }
  disabled={loadingCategories}
>
  <SelectTrigger id="category">
    <SelectValue placeholder={loadingCategories ? "Loading categories..." : "Select a category"} />
  </SelectTrigger>
  <SelectContent>
    {/* Safe default option */}
    <SelectItem value="none">No Category</SelectItem>

    {Array.isArray(categories) && categories.length > 0 &&
      categories
        .filter((category: Category) => category?.id != null && category?.name?.trim())
        .map((category: Category) => {
          const safeId = category.id.toString();
          const safeName = category.name.trim();
          return (
            <SelectItem key={`modal-cat-${category.id}`} value={safeId}>
              {safeName}
              {category.name_ar?.trim() && ` (${category.name_ar.trim()})`}
            </SelectItem>
          );
        })
    }
  </SelectContent>
</Select>

            </div>

            <div className="grid gap-2">
              <Label htmlFor="active-ingredient">Active Ingredient</Label>
              <Input
                id="active-ingredient"
                value={formData.active_ingredient}
                onChange={(e) => setFormData({ ...formData, active_ingredient: e.target.value })}
                placeholder="Enter active ingredient"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="registration-number">Registration Number</Label>
              <Input
                id="registration-number"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                placeholder="Enter registration number"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="crop">Crop</Label>
              <Input
                id="crop"
                value={formData.crop}
                onChange={(e) => setFormData({ ...formData, crop: e.target.value })}
                placeholder="Enter crop"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Enter notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-[#3B82F6] hover:bg-[#2563EB]">
              {product ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
